// src/CalendarPanel.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarPanel.css';
import { db } from './firebase';
import { collection, query, where, onSnapshot, Timestamp, serverTimestamp, addDoc, updateDoc, doc, deleteDoc, getDocs } from 'firebase/firestore';
import { useUser } from './UserContext';
import { EventModal } from './EventModal';
import { toast } from 'react-toastify';
import { createNotification } from './notificationService';

// Icone per navigazione più pulita (opzionale, uso testo se non hai react-icons qui)
const PREV_ICON = '<';
const NEXT_ICON = '>';

moment.locale('it');
const localizer = momentLocalizer(moment);

const messages = {
  previous: '<',
  next: '>',
  today: 'Oggi',
  month: 'Mese',
  week: 'Settimana',
  day: 'Giorno',
  agenda: 'Agenda',
  showMore: total => `+${total}`,
  noEventsInRange: 'Nessun evento in questo periodo',
};

// Tooltip e Stili (Invariati)
const getEventTooltip = (event) => {
    let tooltip = `Creato da: ${event.ownerName || 'Sconosciuto'}`;
    if (event.type === 'personal' && event.sharedWith && event.sharedWith.length > 0) {
        tooltip += `\nCondiviso con: ${event.sharedWith.length} utent${event.sharedWith.length > 1 ? 'i' : 'e'}`;
    }
    return tooltip;
};

const getEventStyle = (event, currentUserId) => {
    let style = {
        backgroundColor: '#0d6efd',
        borderRadius: '3px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '0.6rem',
        padding: '1px 3px'
    };
     if (event.type === 'personal') {
            if (event.ownerId === currentUserId) {
                style.backgroundColor = '#198754';
            } else {
                style.backgroundColor = '#ffc107';
                style.color = '#333';
            }
        } else {
             style.backgroundColor = '#6c757d';
        }
    return style;
};

const CalendarPanel = ({ isOpen, onClose }) => {
    const { userProfile } = useUser();
    const [events, setEvents] = useState([]);
    const [viewMode, setViewMode] = useState('shared');
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEventInfo, setSelectedEventInfo] = useState(null);
    
    // Stato Data
    const [currentDate, setCurrentDate] = useState(new Date());
    
    const [staffListForSharing, setStaffListForSharing] = useState([]);

    const canManageShared = ['completo', 'semi-completo'].includes(userProfile?.permissions?.calendar)
     || userProfile?.role === 'Super Admin';
    const canDeleteShared = userProfile?.permissions?.calendar === 'completo' || userProfile?.role === 'Super Admin';

    // Generazione opzioni per Select
    const months = moment.months();
    const currentYear = moment().year();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i); // Dal corrente -5 al corrente +5

    // Fetch Staff
     useEffect(() => {
        const fetchStaff = async () => {
             if (userProfile && userProfile.centerId) {
                const staffSnapshot = await getDocs(collection(db, 'centers', userProfile.centerId, 'staff'));
                const staffOptions = staffSnapshot.docs
                    .map(doc => ({ value: doc.id, label: doc.data().name }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                setStaffListForSharing(staffOptions);
            }
        };
        fetchStaff();
    }, [userProfile]);

    // Fetch Eventi
   useEffect(() => {
    if (!userProfile || !userProfile.centerId) return;
    setIsLoading(true);
        const eventsCollectionRef = collection(db, 'centers', userProfile.centerId, 'calendarEvents');
        let q;
        if (viewMode === 'shared') {
            q = query(eventsCollectionRef, where('type', '==', 'shared'));
        } else {
            q = query(eventsCollectionRef, where('type', '==', 'personal'));
        }
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            let fetchedEvents = querySnapshot.docs.map(doc => {
                 const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    start: data.start?.toDate(),
                    end: data.end?.toDate(),
                };
            }).filter(event => event.start && event.end);

            if (viewMode === 'personal') {
                 fetchedEvents = fetchedEvents.filter(event =>
                    event.ownerId === userProfile.uid || (event.sharedWith && event.sharedWith.includes(userProfile.uid))
                );
            }
             fetchedEvents = fetchedEvents.map(event => ({
                ...event,
                style: getEventStyle(event, userProfile.uid)
            }));
            setEvents(fetchedEvents);
            setIsLoading(false);
        }, (error) => {
             console.error("Errore nel leggere gli eventi:", error);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [viewMode, userProfile]);

    // --- NUOVO: Filtra e Ordina Eventi per la lista inferiore ---
    const currentMonthEvents = useMemo(() => {
        const startOfMonth = moment(currentDate).startOf('month');
        const endOfMonth = moment(currentDate).endOf('month');

        return events
            .filter(event => {
                const eventStart = moment(event.start);
                // Includi evento se inizia nel mese O se è in corso nel mese
                return eventStart.isBetween(startOfMonth, endOfMonth, null, '[]');
            })
            .sort((a, b) => a.start - b.start); // Ordina per data crescente
    }, [events, currentDate]);


    // Handlers Navigazione
    const handleMonthChange = (e) => {
        const newMonth = parseInt(e.target.value);
        const newDate = moment(currentDate).month(newMonth).toDate();
        setCurrentDate(newDate);
    };

    const handleYearChange = (e) => {
        const newYear = parseInt(e.target.value);
        const newDate = moment(currentDate).year(newYear).toDate();
        setCurrentDate(newDate);
    };

    const handlePrevMonth = () => {
        setCurrentDate(moment(currentDate).subtract(1, 'month').toDate());
    };

    const handleNextMonth = () => {
        setCurrentDate(moment(currentDate).add(1, 'month').toDate());
    };

    // RBC Callbacks
    const handleSelectSlot = (slotInfo) => {
        if (viewMode === 'shared' && !canManageShared) {
            toast.warn("Non hai i permessi per creare eventi nel calendario condiviso.");
            return;
        }
        setSelectedEventInfo({
            start: slotInfo.start,
            end: slotInfo.end,
            allDay: moment(slotInfo.end).isSame(moment(slotInfo.start).add(1, 'day'), 'minute'),
            type: viewMode,
        });
        setIsModalOpen(true);
     };

    const handleSelectEvent = (event) => {
         setSelectedEventInfo(event);
        setIsModalOpen(true);
     };

    const handleAddNewEventClick = () => {
         if (viewMode === 'shared' && !canManageShared) {
            toast.warn("Non hai i permessi per creare eventi nel calendario condiviso.");
            return;
        }
        // Imposta data di inizio basata sulla data corrente visualizzata
        const start = moment(currentDate).hour(9).minute(0).toDate();
        const end = moment(start).add(1, 'hour').toDate();
        
        setSelectedEventInfo({
            start: start,
            end: end,
            allDay: false,
            type: viewMode,
        });
        setIsModalOpen(true);
     };

    const handleSaveEvent = async (eventData) => {
         setIsLoading(true);
         const isCreating = !eventData.id;
         let originalEvent = null;
         if (!isCreating) {
             originalEvent = selectedEventInfo;
         }

         const dataToSave = {
             title: eventData.title,
             start: Timestamp.fromDate(eventData.start),
             end: Timestamp.fromDate(eventData.end),
             allDay: eventData.allDay,
             type: eventData.type,
             sharedWith: eventData.sharedWith || [],
             notes: eventData.notes || '', // <--- NUOVO CAMPO NOTE AGGIUNTO QUI
             ownerId: isCreating ? userProfile.uid : originalEvent?.ownerId,
             ownerName: isCreating ? userProfile.name : originalEvent?.ownerName,
             createdAt: isCreating ? serverTimestamp() : undefined,
             recurrenceRule: eventData.recurrenceRule,
             centerId: userProfile.centerId,
         };
         
         // Pulizia chiavi undefined
         Object.keys(dataToSave).forEach(key => dataToSave[key] === undefined && delete dataToSave[key]);

         try {
             let eventId = eventData.id;
             if (isCreating) {
                 const docRef = await addDoc(collection(db, 'centers', userProfile.centerId, 'calendarEvents'), dataToSave);
                 eventId = docRef.id;
                 toast.success("Evento creato!");
             } else {
                 const eventDocRef = doc(db, 'centers', userProfile.centerId, 'calendarEvents', eventData.id);
                 const updateData = { ...dataToSave };
                 delete updateData.ownerId;
                 delete updateData.ownerName;
                 await updateDoc(eventDocRef, updateData);
                 toast.success("Evento aggiornato!");
             }

             if (eventData.type === 'personal' && eventId) {
                const originalSharedWith = originalEvent?.sharedWith || [];
                 const newSharedWith = dataToSave.sharedWith || [];
                 const newlySharedUserIds = newSharedWith.filter(userId => !originalSharedWith.includes(userId));
                 if (newlySharedUserIds.length > 0) {
                     newlySharedUserIds.forEach(recipientId => {
                         if (recipientId !== userProfile.uid) {
                             createNotification(
                                 recipientId, userProfile,
                                 `ha condiviso un evento (${eventData.title}) con te.`, '/calendario'
                             );
                         }
                     });
                 }
             }
            setIsModalOpen(false);
             setSelectedEventInfo(null);
         } catch (error) {
            console.error("Errore salvataggio evento:", error);
            toast.error("Errore durante il salvataggio dell'evento.");
         } finally {
              setIsLoading(false);
         }
     };

    const handleDeleteEvent = async (eventId) => {
         if (!eventId) return;
         const eventToDelete = events.find(e => e.id === eventId);
         const isOwner = eventToDelete?.ownerId === userProfile.uid;
         const canDeleteThis = isOwner || (eventToDelete?.type === 'shared' && canDeleteShared);

         if (!canDeleteThis) {
             toast.error("Non hai i permessi per eliminare questo evento.");
             return;
         }

         if (!window.confirm("Sei sicuro di voler eliminare questo evento?")) return;

         setIsLoading(true);
         try {
             const eventDocRef = doc(db, 'centers', userProfile.centerId, 'calendarEvents', eventId);
             await deleteDoc(eventDocRef);
             toast.warn("Evento eliminato.");
             setIsModalOpen(false);
             setSelectedEventInfo(null);
         } catch (error) {
            console.error("Errore eliminazione evento:", error);
            toast.error("Errore durante l'eliminazione dell'evento.");
         } finally {
             setIsLoading(false);
         }
     };

    const handleNavigate = useCallback((newDate) => {
        setCurrentDate(newDate);
    }, []);

    const overlayClass = isOpen ? 'sticky-notes-overlay open' : 'sticky-notes-overlay';
    const panelClass = isOpen ? 'sticky-notes-panel open' : 'sticky-notes-panel';

    return (
        <div className={overlayClass} onMouseDown={onClose}>
            {/* Pannello */}
            <div className={panelClass} onMouseDown={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column' }}>
                
                {/* --- 1. MODALE "DENTRO" LA BARRA --- */}
                {/* Questo div agisce da wrapper assoluto per coprire il pannello quando il modale è aperto */}
                {isModalOpen && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1000, // Superiore al contenuto del pannello
                        backgroundColor: 'rgba(255,255,255,0.98)', // Sfondo quasi opaco
                        padding: '20px',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                         {/* Passiamo onClose locale per chiudere solo il modale interno */}
                        <EventModal
                            eventInfo={selectedEventInfo}
                            onSave={handleSaveEvent}
                            onClose={() => { setIsModalOpen(false); setSelectedEventInfo(null); }}
                            onDelete={handleDeleteEvent}
                            staffList={staffListForSharing}
                            // Nota: EventModal potrebbe avere bisogno di modifiche CSS se usa position:fixed.
                            // Qui confidiamo che si adatti al contenitore padre relativo o che la sovrapposizione funzioni.
                        />
                    </div>
                )}

                <div className="sticky-notes-header">
                    <h3>Calendario Rapido</h3>
                    <button onClick={onClose} className="close-notes-btn">✖</button>
                </div>

                <div className="calendar-panel-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                    
                    {/* Header Toggle Viste */}
                    <div className="calendar-panel-header">
                        <div className="calendar-panel-view-toggle">
                            <button
                                className={viewMode === 'shared' ? 'active' : ''}
                                onClick={() => setViewMode('shared')}
                                disabled={isLoading}
                            >
                                Condiviso
                            </button>
                            <button
                                className={viewMode === 'personal' ? 'active' : ''}
                                onClick={() => setViewMode('personal')}
                                disabled={isLoading}
                            >
                                Personale
                            </button>
                        </div>
                        {(viewMode === 'personal' || (viewMode === 'shared' && canManageShared)) && (
                                <button className="calendar-panel-add-btn" onClick={handleAddNewEventClick}>
                                    + Nuovo
                                </button>
                        )}
                    </div>

                    {/* --- 2. SELETTORI MESE/ANNO --- */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: '10px',
                        padding: '0 5px'
                    }}>
                        <button onClick={handlePrevMonth} className="rbc-btn-group" style={{padding: '5px 10px'}}>{PREV_ICON}</button>
                        
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <select 
                                value={moment(currentDate).month()} 
                                onChange={handleMonthChange}
                                style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                            >
                                {months.map((m, i) => (
                                    <option key={i} value={i}>{m}</option>
                                ))}
                            </select>

                            <select 
                                value={moment(currentDate).year()} 
                                onChange={handleYearChange}
                                style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                            >
                                {years.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>

                        <button onClick={handleNextMonth} className="rbc-btn-group" style={{padding: '5px 10px'}}>{NEXT_ICON}</button>
                    </div>

                    {/* Calendario - ALTEZZA FISSA AGGIUNTA */}
                    <div className="calendar-panel-rbc-wrapper" style={{ height: '350px', flex: 'none' }}>
                        {isLoading ? <p>Caricamento...</p> : (
                            <Calendar
                                localizer={localizer}
                                events={events}
                                startAccessor="start"
                                endAccessor="end"
                                views={['month']}
                                defaultView={Views.MONTH}
                                messages={messages}
                                selectable
                                onSelectSlot={handleSelectSlot}
                                onSelectEvent={handleSelectEvent}
                                eventPropGetter={(event) => ({ style: event.style })}
                                tooltipAccessor={getEventTooltip}
                                date={currentDate}
                                onNavigate={handleNavigate}
                                components={{
                                    toolbar: () => null 
                                }}
                            />
                        )}
                    </div>

                    {/* --- 3. LISTA EVENTI SUBITO SOTTO --- */}
                    {/* MARGINE RIDOTTO (10px) E BORDO SUPERIORE */}
                    <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#555' }}>
                            Eventi di {moment(currentDate).format('MMMM YYYY')}:
                        </h4>
                        
                        {currentMonthEvents.length === 0 ? (
                            <p style={{ fontSize: '0.8rem', color: '#999', textAlign: 'center' }}>Nessun evento questo mese.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {currentMonthEvents.map(event => (
                                    <div 
                                        key={event.id} 
                                        onClick={() => handleSelectEvent(event)}
                                        style={{
                                            padding: '8px',
                                            borderRadius: '5px',
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: `4px solid ${event.style?.backgroundColor || '#ccc'}`,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <div style={{ overflow: 'hidden' }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{event.title}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                                {moment(event.start).format('DD MMM')} • {moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}
                                            </div>
                                        </div>
                                        <div style={{ fontSize: '1.2rem', color: '#ccc' }}>›</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default CalendarPanel;